/**
 * Events the creative preview window reports. The frame that renders the
 * creative collects them from adn.js callbacks and the creative's own
 * postMessages, and forwards each one to the window around it.
 */

/** Events every creative can fire, listed in the summary even before they fire. */
export const STANDARD_PREVIEW_EVENTS = [
  'response',
  'pageLoad',
  'impression',
  'visible',
  'viewable',
  'resize',
  'interaction',
  'click',
  'error',
] as const

export type StandardPreviewEvent = (typeof STANDARD_PREVIEW_EVENTS)[number]

export interface PreviewEvent {
  /** A standard event, or `custom` for one the creative sends itself. */
  kind: StandardPreviewEvent | 'custom'
  /** The custom event's type; unset for standard events. */
  name?: string
  /** Extra information, such as the click-through URL or the new size. */
  detail?: string
  /** Milliseconds since the frame started loading the creative. */
  time: number
}

/** Marks the frame's messages so the window ignores anything else posted to it. */
export const PREVIEW_EVENT_SOURCE = 'adn-rui-creative-preview'

export interface PreviewEventMessage {
  source: typeof PREVIEW_EVENT_SOURCE
  event: PreviewEvent
}

export function isPreviewEventMessage(data: unknown): data is PreviewEventMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { source?: unknown }).source === PREVIEW_EVENT_SOURCE
  )
}

export type PreviewDevice = 'desktop' | 'mobile'

/** The viewport the mobile preview emulates — a common modern phone, in CSS pixels. */
export const MOBILE_VIEWPORT = { width: 390, height: 844 }
